<template>
  <div
    v-if="!isPosOnlyMode"
    class="bg-white dark:bg-gray-800/50 rounded-lg shadow overflow-hidden"
  >
    <div class="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <svg
            class="w-5 h-5 text-gray-600 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </div>
        <div>
          <h2 class="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            Configuración General
          </h2>
          <p class="text-xs text-gray-600 dark:text-gray-400">
            Opciones generales del restaurante
          </p>
        </div>
      </div>
    </div>

    <div class="p-3 sm:p-4 space-y-4">
      <div class="space-y-4">
        <div>
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">
                Habilitar IVA
              </h3>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Activa la opción de agregar IVA cuando los clientes soliciten factura
              </p>
            </div>
            <div class="ml-4">
              <button
                :disabled="isSaving"
                :class="[
                  ivaEnabled
                    ? 'bg-indigo-600 hover:bg-indigo-700'
                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
                  'relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
                ]"
                @click="restaurantSettings.toggleIvaEnabled"
              >
                <span
                  :class="[
                    ivaEnabled ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0',
                    'pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out'
                  ]"
                />
              </button>
            </div>
          </div>
        </div>

        <div v-if="ivaEnabled">
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
                Tasa de IVA
              </h4>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Selecciona la tasa de IVA aplicable para tu restaurante
              </p>
            </div>
            <div class="ml-4">
              <div class="flex gap-2">
                <button
                  :disabled="isSaving"
                  :class="[
                    ivaRate === 0.16
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-indigo-300',
                    'px-3 py-1.5 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  ]"
                  @click="restaurantSettings.updateIvaRate(0.16)"
                >
                  16%
                </button>
                <button
                  :disabled="isSaving"
                  :class="[
                    ivaRate === 0.08
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-indigo-300',
                    'px-3 py-1.5 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  ]"
                  @click="restaurantSettings.updateIvaRate(0.08)"
                >
                  8%
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ t('app.subscription.settings.dine_in_without_table.title') }}
            </h3>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {{ t('app.subscription.settings.dine_in_without_table.description') }}
            </p>
          </div>
          <div class="ml-4">
            <button
              :disabled="isSaving"
              :class="[
                allowDineInWithoutTable
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
                'relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
              ]"
              @click="restaurantSettings.toggleDineInWithoutTable"
            >
              <span
                :class="[
                  allowDineInWithoutTable ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0',
                  'pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out'
                ]"
              />
            </button>
          </div>
        </div>
      </div>

      <div>
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ t('app.subscription.settings.takeaway_without_customer.title') }}
            </h3>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {{ t('app.subscription.settings.takeaway_without_customer.description') }}
            </p>
          </div>
          <div class="ml-4">
            <button
              :disabled="isSaving"
              :class="[
                allowTakeawayWithoutCustomer
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
                'relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
              ]"
              @click="restaurantSettings.toggleTakeawayWithoutCustomer"
            >
              <span
                :class="[
                  allowTakeawayWithoutCustomer ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0',
                  'pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out'
                ]"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOperationMode } from '@/composables/useOperationMode'
import type { useRestaurantSettings } from '@/composables/useRestaurantSettings'

const props = defineProps<{
  restaurantSettings: ReturnType<typeof useRestaurantSettings>
}>()

const { t } = useI18n()
const restaurantSettings = props.restaurantSettings
const { isPosOnlyMode } = useOperationMode()

const isSaving = computed(() => restaurantSettings.savingSettings.value)
const ivaEnabled = computed(() => restaurantSettings.ivaEnabled?.value ?? false)
const ivaRate = computed(() => restaurantSettings.ivaRate?.value ?? 0.16)
const allowDineInWithoutTable = computed(() => restaurantSettings.allowDineInWithoutTable.value)
const allowTakeawayWithoutCustomer = computed(() => restaurantSettings.allowTakeawayWithoutCustomer.value)
</script>
